import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authAPI } from '../services/api';
import { FiMail, FiArrowLeft, FiCheckCircle } from 'react-icons/fi';
import logo from '../assets/logo-auth.png';
import './Auth.css';

/**
 * Forgot Password Page
 *
 * ARCHITECTURE: This project uses AWS-based authentication.
 * Password reset is handled by our backend + AWS email service.
 * NO external auth providers (Supabase, Firebase, etc.) are used.
 *
 * Flow:
 * 1. User enters email
 * 2. Backend generates secure token, stores hash in DB
 * 3. Email sent via AWS SES with reset link
 * 4. Generic success message shown (no email enumeration)
 */
const ForgotPassword = () => {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Check if this is admin forgot password flow
  const isAdminFlow = location.pathname.startsWith('/admin');
  const loginPath = isAdminFlow ? '/admin/login' : '/login';

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);

    try {
      await authAPI.forgotPassword(email);
      // Always show success to prevent email enumeration
      setEmailSent(true);
      toast.success('Password reset email sent!');
    } catch (error) {
      // Still show success UI for security (prevent enumeration)
      // But log the error for debugging
      console.error('Password reset error:', error);
      setEmailSent(true);
      toast.info('If an account exists with this email, you will receive a reset link.');
    } finally {
      setLoading(false);
    }
  };

  // Success state after email sent
  if (emailSent) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <img src={logo} alt="Hisab OK Hai" className="auth-logo" />
            <h1>Hisab OK Hai</h1>
          </div>

          <div className="email-sent-success">
            <FiCheckCircle size={64} color="#27AE60" style={{ marginBottom: 20 }} />
            <h2>Check Your Email</h2>
            <p style={{ color: '#7F8C8D', marginBottom: 24 }}>
              We've sent a password reset link to:
              <br />
              <strong>{email}</strong>
            </p>
            <p style={{ color: '#95A5A6', fontSize: '0.9rem', marginBottom: 24 }}>
              Didn't receive the email? Check your spam folder or wait a few minutes.
              <br />
              The link expires in <strong>1 hour</strong>.
            </p>
            <button
              className="btn btn-secondary btn-block"
              onClick={() => setEmailSent(false)}
              style={{ marginBottom: 12 }}
            >
              Try Different Email
            </button>
            <Link to={loginPath} className="btn btn-link">
              <FiArrowLeft style={{ marginRight: 8 }} />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <img src={logo} alt="Hisab OK Hai" className="auth-logo" />
          <h1>Hisab OK Hai</h1>
          <p className="tagline">Reset your password</p>
        </div>

        <h2>Forgot Password</h2>
        <p style={{ color: '#7F8C8D', marginBottom: 24, fontSize: '0.9rem' }}>
          Enter your email address and we'll send you a link to reset your password.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">
              <FiMail style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
              autoFocus
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        <div className="auth-footer" style={{ marginTop: 24 }}>
          <Link to={loginPath} className="btn btn-link">
            <FiArrowLeft style={{ marginRight: 8 }} />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
